import { useEffect, useState } from "react";
import { Button, Flex, Input, Typography, theme } from "@murm/ant";
import { composeBandDisplay } from "../../lib/search/composeBandDisplay";
import { formatAggregationLabel } from "../../lib/search/formatAggregationLabel";
import { trackEvent } from "../../lib/analytics";
import { PENDO_ID, PENDO_EVENT, pendoId } from "../../lib/pendoIds";
import styles from "./BandSelector.module.css";

const { Text, Link } = Typography;

const DEFAULT_RANGE = [0, 100];

// Real bug report: a 0-1 modeled score (e.g. Presidential General Turnout Score, whose `sql`
// divides its raw column by 100 -- confirmed against real data) got the same 0-20/21-40/.../81-100
// percentile-style bands as an ordinary 0-100 score, since nothing read a
// feature's own scale -- there was none to read (see api/types/features/continuous.py's `range`
// field, added alongside this). Dividing `range` into 5 even bands reproduces the old hardcoded
// constant exactly for the default [0, 100] (confirmed: 0-20, 21-40, 41-60, 61-80, 81-100), so
// every existing score is unaffected.
//
// Integer ranges use the old non-overlapping "+1" convention (21-40, not 20-40) -- a percentile
// score's bands shouldn't overlap at the boundary. A fractional range (0-1) has no meaningful
// "+1", so its bands just touch (0-0.2, 0.2-0.4, ...).
function computeBandsFromRange(range) {
  // `range` defaulting via a parameter default only covers `undefined` -- the backend's own
  // `range` field on an ordinary score is explicitly `null` (not absent), which a plain default
  // parameter doesn't catch.
  const [min, max] = range ?? DEFAULT_RANGE;
  const step = (max - min) / 5;
  const isIntegerRange = Number.isInteger(min) && Number.isInteger(max) && Number.isInteger(step);
  const format = (n) => (isIntegerRange ? n : Number(n.toFixed(2)));

  return Array.from({ length: 5 }, (_, i) => {
    const lo = min + i * step;
    const hi = min + (i + 1) * step;
    return `${format(i === 0 ? lo : lo + (isIntegerRange ? 1 : 0))}-${format(hi)}`;
  });
}

/**
 * The config step for a band-style token (continuous/score metrics, Buckets, Age) -- brand-new
 * UI, nothing like it exists elsewhere in the app (MetricCard.js only offers
 * aggregation buttons, never value bands). Renders as CommandPalette's opaque `configPanel`
 * node; CommandPalette has no idea this is bands, and this component has no idea it's inside a
 * command palette.
 *
 * Generalized so Age can reuse it with its own labels: `bands`, when passed explicitly (Age's own
 * preset list), always wins; otherwise bands are derived from `range` (a continuous metric's own
 * [min, max], defaulting to the ordinary 0-100 score scale -- see computeBandsFromRange above).
 * `allLabel` is the default button's text (Buckets/Scores read "All"; Age reads
 * "All Ages"). `allDisplay`/`bandedPrefix` pass straight through to composeBandDisplay for the
 * *composed token text* specifically -- Age's is a different template ("All Ages" / "Ages
 * {bands}"), not just different band labels, so this can't be derived from `allLabel` alone.
 *
 * `allLabel`, specific preset bands, and a custom range are mutually exclusive ("... OR SET A
 * CUSTOM RANGE") — picking one clears the other two, enforced here via the three setters below.
 *
 * `initialSelection` (`{isAll, selectedPresets, customRange, aggregation}`, the exact shape
 * `onChange` itself emits) restores a re-opened token's current selection instead of always
 * starting fresh at "All" -- without it, closing and reopening config on an already-configured
 * token silently lost which bands were active (a real bug: the chip's own text still read e.g.
 * "...of 41-60", but the panel showed "All" as selected, and picking anything new started from a
 * false baseline).
 *
 * `availableAggregations` (e.g. `["sum", "avg"]`, a continuous metric's own field) adds a
 * "PRESENT THE DATA BY" step, shown only while "All" is selected and there's a genuine choice
 * (more than one option) -- picking a specific band or custom range answers "which values"
 * already, so the aggregation choice hides once one is active (2026-09-29: "if local voter
 * score is band all, then sum/average should show. If specific bands then no need to show
 * sum/average"). Defaults to the first option, same as MetricCard.js's `hideOptions` default --
 * there's always a value to compose into the display text, never an unset/blank aggregation.
 * This is a second, independent selection-time home for the same choice that moved to the
 * results page's toggle, not a replacement for it -- both now exist.
 */
const BandSelector = ({
  tokenType,
  metricDisplay,
  onChange,
  bands,
  range,
  allLabel = "All",
  allDisplay,
  bandedPrefix,
  initialSelection,
  availableAggregations = [],
}) => {
  const { token } = theme.useToken();
  // `?? DEFAULT_RANGE`, not a parameter default -- a feature with no override serializes its
  // `range` as an explicit `null`, not an absent/undefined prop.
  const effectiveRange = range ?? DEFAULT_RANGE;
  // `bands` (Age's own preset list) always wins when passed explicitly; otherwise bands are
  // derived from `range`, which defaults to the ordinary 0-100 score scale.
  const effectiveBands = bands ?? computeBandsFromRange(effectiveRange);
  const [isAll, setIsAll] = useState(initialSelection?.isAll ?? true);
  const [selectedPresets, setSelectedPresets] = useState(initialSelection?.selectedPresets ?? []);
  const [customRange, setCustomRange] = useState(initialSelection?.customRange ?? null);
  const [minInput, setMinInput] = useState(initialSelection?.customRange?.split("-")[0] ?? "");
  const [maxInput, setMaxInput] = useState(initialSelection?.customRange?.split("-")[1] ?? "");
  const [aggregation, setAggregation] = useState(
    initialSelection?.aggregation ?? availableAggregations[0]
  );
  // Only while "All" is active -- once a specific band or custom range is picked, that already
  // answers "which values", so the aggregation choice hides. Still requires a genuine choice
  // (more than one option).
  const showAggregationToggle = isAll && availableAggregations.length > 1;

  const emit = (next, nextAggregation = aggregation) => {
    const withAggregation = { ...next, aggregation: nextAggregation };
    const bandsDisplay = composeBandDisplay(metricDisplay, next, { allDisplay, bandedPrefix });
    // `next.isAll`, not the outer `showAggregationToggle` -- togglePreset/applyCustomRange call
    // this synchronously in the same tick as `setIsAll(false)`, before that state update commits,
    // so the outer closure's `isAll` is still stale (true) here.
    const showsAggregation = next.isAll && availableAggregations.length > 1;
    const display = showsAggregation
      ? `${bandsDisplay} by ${formatAggregationLabel(nextAggregation)}`
      : bandsDisplay;
    onChange?.({ ...withAggregation, display });
  };

  const pickAll = () => {
    trackEvent(PENDO_EVENT.QUERY_BAND_SELECTED, { tokenType, band: "all" });
    setIsAll(true);
    setSelectedPresets([]);
    setCustomRange(null);
    emit({ isAll: true, selectedPresets: [], customRange: null });
  };

  const togglePreset = (band) => {
    trackEvent(PENDO_EVENT.QUERY_BAND_SELECTED, {
      tokenType,
      band,
      selected: !selectedPresets.includes(band),
    });
    const next = selectedPresets.includes(band)
      ? selectedPresets.filter((b) => b !== band)
      : [...selectedPresets, band];
    setIsAll(false);
    setSelectedPresets(next);
    setCustomRange(null);
    emit({ isAll: false, selectedPresets: next, customRange: null });
  };

  const canApplyCustomRange = Boolean(minInput.trim()) && Boolean(maxInput.trim());

  const applyCustomRange = () => {
    const range = `${minInput.trim()}-${maxInput.trim()}`;
    setIsAll(false);
    setSelectedPresets([]);
    setCustomRange(range);
    emit({ isAll: false, selectedPresets: [], customRange: range });
  };

  const pickAggregation = (nextAggregation) => {
    setAggregation(nextAggregation);
    emit({ isAll, selectedPresets, customRange }, nextAggregation);
  };

  // Real gap ("missing run your search part" Figma): the chip kept showing the term's bare
  // display ("Age") until the user touched something, even though there's already a real,
  // composed default ("All Ages" / "Local Voter Score by Sum") to show immediately -- the same
  // Figma shows that default already reflected in the chip the moment SELECTED opens, not only
  // after an explicit pick. Propagates once on mount; each setter's own `emit` call already
  // covers every state change after that.
  useEffect(() => {
    emit({ isAll, selectedPresets, customRange });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only, deliberately
  }, []);

  return (
    <div className={styles.panel}>
      {showAggregationToggle && (
        <>
          <Text type="secondary" className={styles.label}>
            PRESENT THE DATA BY
          </Text>
          <Flex gap={token.marginXXS} wrap className={`${styles.row} ${styles.rowSpaced}`}>
            {availableAggregations.map((option) => (
              <Button
                {...pendoId(PENDO_ID.BAND_AGGREGATION, option)}
                key={option}
                size="small"
                type={aggregation === option ? "primary" : "default"}
                onClick={() => pickAggregation(option)}
              >
                {formatAggregationLabel(option)}
              </Button>
            ))}
          </Flex>
        </>
      )}
      <Text type="secondary" className={styles.label}>
        SELECT ONE OR MORE BANDS
      </Text>
      <Flex gap={token.marginXXS} wrap className={styles.row}>
        <Button
          {...pendoId(PENDO_ID.BAND_ALL)}
          size="small"
          type={isAll ? "primary" : "default"}
          onClick={pickAll}
        >
          {allLabel}
        </Button>
        {effectiveBands.map((band) => (
          <Button
            {...pendoId(PENDO_ID.BAND_PRESET)}
            key={band}
            size="small"
            type={!isAll && selectedPresets.includes(band) ? "primary" : "default"}
            onClick={() => togglePreset(band)}
          >
            {band}
          </Button>
        ))}
      </Flex>

      <Text type="secondary" className={`${styles.label} ${styles.labelSpaced}`}>
        OR SET A CUSTOM RANGE
      </Text>
      <Flex align="center" gap={token.marginXXS} className={styles.row}>
        <Input
          size="small"
          placeholder={String(effectiveRange[0])}
          value={minInput}
          onChange={(e) => setMinInput(e.target.value)}
          className={styles.rangeInput}
          aria-label="Custom range minimum"
        />
        <span>to</span>
        <Input
          size="small"
          placeholder={String(effectiveRange[1])}
          value={maxInput}
          onChange={(e) => setMaxInput(e.target.value)}
          className={styles.rangeInput}
          aria-label="Custom range maximum"
        />
        <Link
          {...pendoId(PENDO_ID.BAND_CUSTOM_APPLY)}
          disabled={!canApplyCustomRange}
          onClick={applyCustomRange}
        >
          Apply
        </Link>
      </Flex>
    </div>
  );
};

export default BandSelector;
